import { Injectable } from '@angular/core';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserRole } from '../../core-module/models/role';
import { AppAccessToken } from '../models/app-access-token';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class JwtTokenService {

  private readonly _accessToken: BehaviorSubject<AppAccessToken | null> = new BehaviorSubject<AppAccessToken | null>(null);
  readonly accessToken$ = this._accessToken.asObservable();
  readonly isAdmin$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.Admin)));
  readonly isSecondaryAdmin$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.SecondaryAdmin)));
  readonly isLO$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.LoanOfficer)));
  readonly isProcessor$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.Processor)));
  readonly isBranchManager$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.BranchManager)));
  readonly showManageLoans$ = this.accessToken$.pipe(map(() => this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin])));
  readonly showMyLoans$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.LoanOfficer) && !this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin, UserRole.BranchManager])));
  readonly showMyProcessingPayrolls$ = this.accessToken$.pipe(map(() => this.isInRole(UserRole.Processor) && !this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin, UserRole.BranchManager])));

  private decodedToken?: { [key: string]: string };

  set accessToken(val: AppAccessToken | null) {
    let decoded: any = null;
    if (val?.access_token) {
      decoded = jwtDecode(val.access_token);
      this.localStorageService.setToken(val);
    }
    this.decodedToken = decoded;
    this._accessToken.next(val);
  }

  get accessToken() {
    let val = this._accessToken.getValue();
    if (!val) {
      let token = this.localStorageService.getToken();
      val = token;
      if (val?.access_token) {
        this.decodedToken = jwtDecode(val.access_token);
      }
    }
    return val;
  }

  constructor(
    private localStorageService: LocalStorageService) {
  }

  //clearToken() {
  //  this.decodedToken = null;
  //}

  getEmail() {
    if (!this.accessToken)
      return "";
    return this.decodedToken?.['email'] ?? '';
  }

  getUserId() {
    if (!this.accessToken)
      return "";
    return this.decodedToken?.['sub'] ?? '';
  }

  getRoles(): UserRole[] {
    if (!this.accessToken)
      return [];
    
    if (Array.isArray(this.decodedToken?.['role'])) {
      return this.decodedToken['role'] as unknown as Array<UserRole>;
    }
    else {
      return new Array(this.decodedToken?.['role']) as unknown as Array<UserRole>;
    }
  }

  getExpiryTime(): number {
    if (!this.accessToken)
      return 0;
    return parseFloat(this.decodedToken!['exp']);
  }

  isTokenExpired(): boolean {
    const expiryTime: number = this.getExpiryTime();
    if (expiryTime) {
      return ((1000 * expiryTime) - (new Date()).getTime()) < 5000;
    } else {
      return false;
    }
  }

  isInRole(role: UserRole): boolean {
    let roles = this.getRoles();
    return roles.indexOf(role) != -1;
  }

  hasEveryRoles(roles: UserRole[]): boolean {
    let rs = this.getRoles();
    return roles.every(r => rs.includes(r));
  }

  hasAnyRoles(roles: UserRole[]): boolean {
    let rs = this.getRoles();
    return roles.some(r => rs.includes(r));
  }

  canManageLoans(): boolean {
    return this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin]);
  }

  canManageMyLoans(): boolean {
    return this.isInRole(UserRole.LoanOfficer) && !this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin, UserRole.BranchManager]);
  }

  canManageMyProcessingPayrolls() {
    return this.isInRole(UserRole.Processor) && !this.hasAnyRoles([UserRole.Admin, UserRole.SecondaryAdmin, UserRole.BranchManager]);
  }

  canManageBranchLoans() {
    return this.isInRole(UserRole.BranchManager) && !this.canManageLoans();
  }

  isAdmin(): boolean {
    return this.isInRole(UserRole.Admin);
  }

  isLoManager(): boolean {
    return this.isInRole(UserRole.SecondaryAdmin);
  }

  isLoanOfficer(): boolean {
    return this.isInRole(UserRole.LoanOfficer);
  }

  isProcessor(): boolean {
    return this.isInRole(UserRole.Processor);
  }

  isFunder(): boolean {
    return this.isInRole(UserRole.Funder);
  }

  // isSclInternalRoles() {
  //   return this.hasAnyRoles(this.roleService.getSclInternalRoles());
  // }
    
  get isExpired(): boolean {
    const expiryTime: number = this.getExpiryTime();
    if (expiryTime) {
      return ((1000 * expiryTime) - (new Date()).getTime()) < 5000;
    } else {
      return false;
    }
  }  
}
